import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Server } from 'lucide-react';
import type { AssetComponentDto } from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { Button } from '@siliconx/shared/ui/Button';
import {
  acceleratorTotalText,
  componentFmvRangeText,
  effectiveSystemFmvLabel,
  notSeenSinceText,
  systemDeltaText,
} from '@siliconx/shared/ui/assetComponentsFooter';
import { formatDate, formatUsd } from '../../lib/format';
import { useAssetComponents } from '../../lib/assetComponents';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { useModalTrap } from '../../routes/assets/useModalTrap';

/**
 * Probe asset grouping (spec section 3, D1): a server keeps its own System
 * FMV from its own SKU, and each accelerator child is priced as if sold on
 * its own. The footer compares the two; it never replaces System FMV.
 */
export function AssetComponentsCard({
  parentAssetId,
  canDetach,
}: {
  parentAssetId: string;
  canDetach: boolean;
}) {
  const { data, isError } = useAssetComponents(parentAssetId);
  const [detaching, setDetaching] = useState<AssetComponentDto | null>(null);

  if (isError) {
    return (
      <Card>
        <PanelTitle title="Components" />
        <p className="p-4 pt-0 text-sm text-muted-foreground">Could not load this server's components.</p>
      </Card>
    );
  }
  if (!data || data.totalCount === 0) return null;

  const delta = systemDeltaText(data.deltaFromSystem);

  return (
    <>
      <Card>
        <PanelTitle
          title="Components"
          description="Each accelerator in this server is its own asset, priced as if sold on its own."
        />
        <div className="space-y-3 p-4 pt-0 text-sm">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">System FMV</span>
            {data.systemFmv ? (
              <>
                <span className="font-mono text-lg font-semibold text-foreground">
                  {formatUsd(data.systemFmv.valueUsd)}
                </span>
                <span className="text-xs text-muted-foreground">priced {formatDate(data.systemFmv.pricedAt)}</span>
              </>
            ) : data.effectiveSystemFmv ? (
              // SIL-1290: chassis SKU unmatched, priced children float the display
              // floor. Never added on top of a chassis snapshot — see the branch above.
              <>
                <span className="font-mono text-lg font-semibold text-foreground">
                  {formatUsd(data.effectiveSystemFmv.valueUsd)}
                </span>
                <span className="text-xs text-muted-foreground">{effectiveSystemFmvLabel(data.effectiveSystemFmv)}</span>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">
                Not priced yet. The server gets its own FMV once its SKU is matched.
              </span>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-1 pr-3">Component</th>
                  <th className="py-1 pr-3">Identity</th>
                  <th className="py-1 pr-3">SKU</th>
                  <th className="py-1 pr-3 text-right">FMV</th>
                  <th className="py-1">Status</th>
                  {canDetach && <th className="py-1 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {data.components.map((c) => (
                  <ComponentRow
                    key={c.assetId}
                    component={c}
                    onDetach={canDetach ? () => setDetaching(c) : undefined}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border">
                  <td colSpan={canDetach ? 6 : 5} className="py-2 text-foreground">
                    <span className="font-medium">{acceleratorTotalText(data)}</span>
                    {delta && <span className="ml-2 text-muted-foreground">{delta}</span>}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </Card>

      {detaching && (
        <DetachComponentDialog
          component={detaching}
          parentAssetId={parentAssetId}
          onClose={() => setDetaching(null)}
        />
      )}
    </>
  );
}

function ComponentRow({
  component: c,
  onDetach,
}: {
  component: AssetComponentDto;
  onDetach?: () => void;
}) {
  const range = c.fmv ? componentFmvRangeText(c.fmv) : null;
  const unmatched = c.skuId === null || c.skuMatchStatus === 'unmatched';

  return (
    <tr className="border-t border-border align-top">
      <td className="py-2 pr-3">
        <Link
          to="/assets/$assetId"
          params={{ assetId: c.assetId }}
          className="font-medium text-foreground hover:underline"
        >
          {c.modelName ?? 'Unidentified accelerator'}
        </Link>
        <div className="text-xs text-muted-foreground">GPU {c.deviceIndex ?? '-'}</div>
      </td>
      <td className="py-2 pr-3">
        <Link
          to="/assets/$assetId"
          params={{ assetId: c.assetId }}
          className="font-mono text-xs text-brand-600 hover:underline"
        >
          {c.serialMasked ?? c.assetId.slice(0, 8)}
        </Link>
      </td>
      <td className="py-2 pr-3">
        {unmatched ? (
          // The component's own page has no match action. While the server is in
          // Customs the model is confirmed from the Customs page (SIL-1898).
          <Link
            to="/assets/$assetId"
            params={{ assetId: c.assetId }}
            className="text-xs font-medium text-amber-700 hover:underline dark:text-amber-400"
          >
            Unmatched
          </Link>
        ) : (
          <div className="text-xs text-foreground">
            <div>{c.skuModelName ?? 'Matched'}</div>
            {c.skuMatchStatus === 'inferred' && (
              <span
                className="mt-0.5 inline-block rounded-full border border-border px-1.5 py-px text-2xs text-muted-foreground"
                title="Inferred match: the model string alone matched this SKU. No serial number and no PCI-subsystem ID confirmed it."
              >
                Inferred match
              </span>
            )}
          </div>
        )}
      </td>
      <td className="py-2 pr-3 text-right">
        {c.fmv ? (
          <>
            <div className="font-mono tabular-nums text-foreground">{formatUsd(c.fmv.valueUsd)}</div>
            {range && <div className="text-xs text-muted-foreground">{range}</div>}
          </>
        ) : (
          <span className="text-xs text-muted-foreground">Not priced</span>
        )}
      </td>
      <td className="py-2 text-xs text-muted-foreground">{c.isMissing ? notSeenSinceText(c.lastSeen) : 'Present'}</td>
      {onDetach && (
        <td className="py-2 text-right">
          <Button variant="secondary" size="sm" onClick={onDetach}>
            Detach
          </Button>
        </td>
      )}
    </tr>
  );
}

const MIN_REASON = 3;

function DetachComponentDialog({
  component,
  parentAssetId,
  onClose,
}: {
  component: AssetComponentDto;
  parentAssetId: string;
  onClose: () => void;
}) {
  const panelRef = useModalTrap(true);
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();

  const detach = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.assets[':assetId'].detach.$post({
        param: { assetId: component.assetId },
        json: { reason: trimmed },
      });
      await throwIfNotOk(res);
    },
    onSuccess: async () => {
      toast.success('Detached. It is now its own asset.');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.assets.components(parentAssetId) }),
        queryClient.invalidateQueries({ queryKey: qk.assets.detail(parentAssetId) }),
        queryClient.invalidateQueries({ queryKey: qk.assets.detail(component.assetId) }),
        queryClient.invalidateQueries({ queryKey: qk.assets.listAll() }),
      ]);
      onClose();
    },
    onError: () => toast.error('Could not detach that component.'),
  });

  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !detach.isPending) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="detach-component-title"
        className="w-full max-w-sm rounded-xl bg-card p-6 shadow-xl"
      >
        <h2 id="detach-component-title" className="mb-2 text-base font-semibold text-foreground">
          Detach {component.modelName ?? 'this accelerator'} (GPU {component.deviceIndex ?? '-'})?
        </h2>
        <p className="mb-3 text-sm text-muted-foreground">
          It becomes its own asset. It keeps its current status and FMV, and stops following this server's
          status and team. Use this when the part has left the chassis.
        </p>
        <label htmlFor="detach-component-reason" className="mb-1 block text-xs font-medium text-foreground">
          Reason
        </label>
        <textarea
          id="detach-component-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          rows={3}
          className="mb-4 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={detach.isPending}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <Button onClick={() => detach.mutate()} disabled={trimmed.length < MIN_REASON || detach.isPending}>
            {detach.isPending ? 'Detaching...' : 'Detach component'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** D7: while attached, a component follows its server; detach it to change that. */
export function InstalledInNotice({
  parentAssetId,
  parentLabel,
  parentIsUnidentifiedChassis,
}: {
  parentAssetId: string;
  parentLabel: string | null;
  /**
   * SIL-1202: the parent is still an unidentified, Customs-held chassis.
   * This asset has ALREADY been moved onto the org's normal team on its own
   * — the "follows this server's status and team" copy below would be wrong
   * here, and the parent is not a page the customer can open, so no Link.
   */
  parentIsUnidentifiedChassis?: boolean;
}) {
  if (parentIsUnidentifiedChassis) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm">
        <Server className="size-4 shrink-0 text-muted-foreground" />
        <span className="text-muted-foreground">Installed in</span>
        <span className="font-medium text-foreground">{parentLabel ?? 'Unidentified chassis'}</span>
        <span className="text-xs text-muted-foreground">
          This component matched our catalogue on its own; the chassis housing it has not been identified yet and
          does not need your attention.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm">
      <Server className="size-4 shrink-0 text-muted-foreground" />
      <span className="text-muted-foreground">Installed in</span>
      <Link
        to="/assets/$assetId"
        params={{ assetId: parentAssetId }}
        className="font-medium text-brand-600 hover:underline"
      >
        {parentLabel ?? parentAssetId.slice(0, 8)}
      </Link>
      <span className="text-xs text-muted-foreground">
        It follows this server's status and team until it is detached.
      </span>
    </div>
  );
}
